import { useSyncExternalStore } from "react";
import { createHitDetector, createTakeScorer } from "./timing";
import { openMicrophone } from "./listener";
import { recordPlay } from "../stats/plays";

// The practice session in progress. Once started, everything played anywhere in the app counts:
// each rhythm's time, its tempos, and its takes (each run from Play to Stop). With the
// microphone on, each take is also scored for accuracy as it plays. The session survives
// moving between pages and reloading; Finish hands it over to be saved or posted.
const SESSION_KEY = "truechops.practiceSession", CALIBRATION_KEY = "truechops.practiceCalibration";
// A take is scored this long after it stops: notes already queued still sound, and their hits
// reach the microphone a little later. Takes under a second don't count.
const GRACE_MS = 500, MIN_TAKE_SECONDS = 1;
const MAX_ITEMS = 30, MAX_TAKES = 40, SHARED_SCORES = 8, MAX_SCORE_BYTES = 18000, STALE_MS = 12 * 3600000;
const IDLE = { active: false, startedAt: null, items: [], playing: null, live: null, lastTake: null, mic: "off", micError: "", finished: null, calibration: null, clips: [] };
// Camera clips of the latest takes (kept in memory only), to attach one when posting.
const MAX_CLIPS = 3;
const storage = {
  read(key) { try { return JSON.parse(window.localStorage.getItem(key) || "null"); } catch { return null; } },
  write(key, value) { try { if (value == null) window.localStorage.removeItem(key); else window.localStorage.setItem(key, JSON.stringify(value)); } catch { /* Private mode: the session lives in memory. */ } },
};
// A rhythm's identity: the same notation under the same name is the same rhythm.
function fingerprint(name, score) {
  let hash = 2166136261;
  for (const character of `${name}|${JSON.stringify(score?.measures || [])}`) { hash ^= character.charCodeAt(0); hash = Math.imul(hash, 16777619) >>> 0; }
  return hash.toString(36);
}

let state = IDLE, take = null, ending = null, recorder = null, microphone = null, detector = null, ticker = null, saveTimer = null, restored = false;
const listeners = new Set(), levelListeners = new Set();
function emit(changes) {
  state = { ...state, ...changes };
  listeners.forEach((listener) => listener());
  clearTimeout(saveTimer);
  // A finished session waits here too, so logging in to save it doesn't lose it.
  saveTimer = setTimeout(() => storage.write(SESSION_KEY, state.active || state.finished ? { ...(state.active ? { startedAt: state.startedAt, items: state.items } : {}), finished: state.finished } : null), 300);
}
function restore() {
  if (restored || typeof window === "undefined") return;
  restored = true;
  const saved = storage.read(SESSION_KEY), calibration = storage.read(CALIBRATION_KEY);
  state = { ...state, calibration, finished: saved?.finished || null,
    ...(saved?.startedAt && Date.now() - saved.startedAt < STALE_MS ? { active: true, startedAt: saved.startedAt, items: saved.items || [] } : {}) };
}
function onPeak(time, peak) {
  levelListeners.forEach((listener) => listener(peak));
  const hit = detector?.(time, peak);
  if (hit) { take?.hear(hit.time); ending?.scorer.hear(hit.time); }
}
// Scores a stopped take and adds it to its rhythm (a take outside a session, `key` null, is only
// scored: it's the last take, and nothing more).
function endTake() {
  if (!ending) return;
  const { scorer, key, name, tempo, seconds, timer } = ending;
  clearTimeout(timer);
  ending = null;
  let scored = null;
  if (scorer) { scorer.settle(Infinity); scored = scorer.summary(); }
  const result = { tempo, seconds: Math.round(seconds), at: Date.now(), ...(scored?.accuracy != null ? scored : { accuracy: null }) };
  emit({ items: key == null ? state.items : state.items.map((item) => item.key === key ? { ...item, takes: [...item.takes, result].slice(-MAX_TAKES) } : item), lastTake: { name, ...result } });
  if (key == null) return;
  // The camera's recording of this take, if it's on.
  recorder?.stop().then((blob) => {
    if (!blob?.size) return;
    const clips = [...state.clips, { id: `${result.at}`, name, tempo, accuracy: result.accuracy, blob, url: URL.createObjectURL(blob) }];
    clips.slice(0, -MAX_CLIPS).forEach((clip) => URL.revokeObjectURL(clip.url));
    emit({ clips: clips.slice(-MAX_CLIPS) });
  });
}
const dropClips = () => state.clips.forEach((clip) => URL.revokeObjectURL(clip.url));
// The take's score so far, a few times a second while it plays.
function followTake() {
  clearInterval(ticker);
  ticker = setInterval(() => { if (take && microphone) { take.settle(microphone.now()); const live = take.summary(); if (live.notes !== state.live?.notes || live.extra !== state.live?.extra) emit({ live }); } }, 250);
}

export const practice = {
  get() { restore(); return state; },
  subscribe(listener) { restore(); listeners.add(listener); return () => listeners.delete(listener); },
  // The microphone's level, many times a second (for a meter).
  onLevel(listener) { levelListeners.add(listener); return () => levelListeners.delete(listener); },
  start() { emit({ active: true, startedAt: Date.now(), items: [], playing: null, live: null, lastTake: null, finished: null }); },
  finish() {
    if (state.playing) practice.playStopped();
    endTake();
    const session = { startedAt: state.startedAt, endedAt: Date.now(), items: state.items };
    emit({ ...IDLE, calibration: state.calibration, mic: state.mic, finished: session.items.length ? session : null, clips: session.items.length ? state.clips : [] });
    return session;
  },
  discard() { if (state.playing) practice.playStopped(); endTake(); dropClips(); emit({ ...IDLE, calibration: state.calibration, mic: state.mic }); },
  clearFinished() { dropClips(); emit({ finished: null, clips: [] }); },
  // The camera, recording each take while it's on: { start(), stop() → Promise<Blob|null>, discard() }.
  setRecorder(next) { recorder = next; },

  // Playback anywhere in the app reports here: a take starts, its notes sound, it stops. `refs`
  // names the rhythms playing (src/lib/stats/refs.js): their plays count whether or not a
  // session is on, and a session counts its time toward them.
  // Without a session, a take is still scored while the microphone listens (Score's own Listen),
  // but nothing is kept beyond its result.
  playStarted({ name, score, tempo, source = "score", refs = [] }) {
    recordPlay(refs);
    if (!state.active && !microphone) return;
    if (state.playing) practice.playStopped();
    const label = String(name || "Untitled rhythm").slice(0, 100);
    if (!state.active) {
      take = createTakeScorer({ offset: state.calibration?.offset ?? microphone.estimatedLatency() });
      emit({ playing: { key: null, name: label, tempo: Math.round(tempo), startedAt: Date.now() }, live: null });
      followTake();
      return;
    }
    const key = fingerprint(label, score);
    const named = [].concat(refs || []);
    const items = state.items.some((item) => item.key === key) ? state.items.map((item) => item.key === key ? { ...item, refs: [...new Set([...(item.refs || []), ...named])].slice(0, 12) } : item) : [...state.items, { key, name: label, source, refs: named.slice(0, 12), seconds: 0, tempos: [], takes: [],
      ...(state.items.filter((item) => item.score).length < SHARED_SCORES && score && JSON.stringify(score).length <= MAX_SCORE_BYTES ? { score } : {}) }].slice(-MAX_ITEMS);
    take = microphone ? createTakeScorer({ offset: state.calibration?.offset ?? microphone.estimatedLatency() }) : null;
    emit({ items, playing: { key, name: label, tempo: Math.round(tempo), startedAt: Date.now() }, live: null });
    recorder?.start();
    followTake();
  },
  expect(time, velocity = 1) { take?.expect(time, velocity); },
  playStopped() {
    const playing = state.playing;
    clearInterval(ticker); ticker = null;
    if (!playing) return;
    const seconds = (Date.now() - playing.startedAt) / 1000, kept = playing.key != null;
    endTake();
    if (seconds >= MIN_TAKE_SECONDS && (kept || take)) ending = { scorer: take, key: playing.key, name: playing.name, tempo: playing.tempo, seconds, timer: setTimeout(endTake, take || (kept && recorder) ? GRACE_MS : 0) };
    else if (kept) recorder?.discard();
    take = null;
    if (!kept) { emit({ playing: null, live: null }); return; }
    const items = state.items.map((item) => item.key !== playing.key ? item : { ...item, seconds: Math.round(item.seconds + seconds),
      tempos: item.tempos.includes(playing.tempo) ? item.tempos : [...item.tempos, playing.tempo] });
    emit({ items, playing: null, live: null });
  },

  // Listening for accuracy.
  async listen(on) {
    if (!on) { microphone?.close(); microphone = null; detector = null; take = null; emit({ mic: "off", micError: "" }); return; }
    if (microphone) return;
    emit({ mic: "starting", micError: "" });
    try {
      microphone = await openMicrophone(onPeak);
      detector = createHitDetector({ threshold: state.calibration?.threshold ?? 0.03 });
      if (state.playing) take = createTakeScorer({ offset: state.calibration?.offset ?? microphone.estimatedLatency() });
      emit({ mic: "on" });
    } catch (error) { microphone = null; emit({ mic: "error", micError: error.message }); }
  },
  setCalibration(calibration) {
    storage.write(CALIBRATION_KEY, calibration);
    if (microphone) detector = createHitDetector({ threshold: calibration.threshold });
    emit({ calibration });
  },
};

export function usePractice() {
  return useSyncExternalStore(practice.subscribe, practice.get, () => IDLE);
}
// What a finished session sends to be saved.
export const sessionBody = (session, { caption = "", post = false } = {}) => ({
  startedAt: new Date(session.startedAt).toISOString(), endedAt: new Date(session.endedAt).toISOString(), caption, post,
  items: session.items.filter((item) => item.seconds > 0).map(({ name, source, refs, seconds, tempos, takes, score }) => ({ name, source, seconds, tempos, takes, ...(refs?.length ? { refs } : {}), ...(score ? { score } : {}) })),
});
export const formatDuration = (seconds) => {
  const total = Math.round(seconds), hours = Math.floor(total / 3600), minutes = Math.floor(total % 3600 / 60);
  return hours ? `${hours} h ${minutes} min` : minutes ? `${minutes} min` : `${total % 60} s`;
};
